import { useState } from "react";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import {
  ShieldCheck, ScanEye, FileSearch, AlertTriangle, BookOpen,
  Upload, Loader2, Sparkles, CheckCircle, XCircle, FileText,
  Search, Percent, Fingerprint, Scale, Globe, Newspaper
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOLS = [
  {
    id: "verify_documents",
    name: "Verify Documents",
    icon: ScanEye,
    color: "bg-emerald-500",
    description: "Instantly verify passports, ID cards, driver's licenses, visas, and other identity documents for more than 254 countries and 16,000 document templates supported",
    hoverTip: "Upload identity documents for AI-powered verification against 16,000+ templates across 254 countries"
  },
  {
    id: "plagiarism_detector",
    name: "Plagiarism Detector",
    icon: FileSearch,
    color: "bg-violet-500",
    description: "Identify AI-generated content in documents and provide a rating. Verify originality, detect plagiarism, identify AI-generated writing, and get feedback",
    hoverTip: "Detect AI-generated content and plagiarism with originality scoring and detailed feedback"
  },
  {
    id: "fake_checker",
    name: "Fake Checker",
    icon: AlertTriangle,
    color: "bg-amber-500",
    description: "Read documents and identify false information, misleading claims, and inaccuracies",
    hoverTip: "Analyze content for misinformation, false claims, and factual inaccuracies"
  },
  {
    id: "narrative_detector",
    name: "Narrative Detector",
    icon: BookOpen,
    color: "bg-blue-600",
    description: "Assess the purpose and characteristics of information — detect agenda-driven content, journalism, wiki, opinion, satire",
    hoverTip: "Identify the true purpose of content — whether it aims to sell, inform, satirize, or persuade"
  }
];

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AiAuthenticator() {
  const auth = useAuth();
  const [activeTool, setActiveTool] = useState(null);
  const [documentContent, setDocumentContent] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    toast.success(`File selected: ${file.name}`);
  };

  const handleAnalyze = async () => {
    if (!activeTool) {
      toast.error("Please select a tool first");
      return;
    }
    if (!documentContent.trim() && !uploadedFile) {
      toast.error("Please provide document content or upload a file");
      return;
    }

    setIsProcessing(true);
    setResult(null);

    try {
      const content = uploadedFile
        ? `[File: ${uploadedFile.name}] ${documentContent || "See attached file for analysis."}`
        : documentContent;

      const res = await axios.post(
        `${API}/agent/hermes/authenticator/analyze`,
        { tool_id: activeTool.id, content },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );

      setResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Analysis failed. Please try again.";
      toast.error(msg);
      setResult({
        status: "error",
        summary: msg,
        details: ["The analysis engine encountered an error."],
        confidence: 0,
        recommendations: ["Please try again with different content."],
      });
    }
    setIsProcessing(false);
  };

  const clearAll = () => {
    setDocumentContent("");
    setUploadedFile(null);
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">
                AI-AUTHENTICATOR
              </h1>
              <p className="text-slate-500">Verify, detect, authenticate and analyze content with AI</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          {TOOLS.map(tool => {
            const Icon = tool.icon;
            const isActive = activeTool?.id === tool.id;
            return (
              <button
                key={tool.id}
                onClick={() => { setActiveTool(tool); setResult(null); }}
                className={`relative group flex flex-col items-center p-4 rounded-xl border transition-all text-center ${
                  isActive
                    ? 'bg-white border-indigo-400 shadow-md ring-2 ring-indigo-200'
                    : 'bg-white border-slate-200 hover:border-indigo-300 hover:shadow-sm'
                }`}
                title={tool.hoverTip}
              >
                <div className={`w-12 h-12 rounded-full ${tool.color} flex items-center justify-center mb-3`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-semibold text-slate-700">{tool.name}</span>
                <span className="text-xs text-slate-400 mt-1">{tool.description.substring(0, 50)}...</span>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 shadow-lg max-w-xs">
                  {tool.hoverTip}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                </div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="space-y-6">
            {activeTool ? (
              <>
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-8 h-8 rounded-full ${activeTool.color} flex items-center justify-center`}>
                      <activeTool.icon className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="font-semibold text-slate-800">{activeTool.name}</h2>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">{activeTool.description}</p>

                  {/* File Upload */}
                  <div className="mb-4">
                    <Label className="text-xs font-medium text-slate-700 mb-2 block">Upload Document</Label>
                    <div className="border-2 border-dashed border-slate-300 p-6 text-center hover:border-indigo-400 transition-colors rounded-lg">
                      <input
                        type="file"
                        accept=".pdf,.docx,.doc,.txt"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="auth-upload"
                      />
                      <label htmlFor="auth-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        {uploadedFile ? (
                          <p className="text-indigo-600 font-medium text-sm">{uploadedFile.name}</p>
                        ) : (
                          <>
                            <p className="text-slate-600 text-sm font-medium">Drop file here or click to browse</p>
                            <p className="text-xs text-slate-400 mt-1">PDF, DOCX, TXT</p>
                          </>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Or Paste Content */}
                  <div className="mb-4">
                    <Label className="text-xs font-medium text-slate-700 mb-2 block">Or Paste Content</Label>
                    <Textarea
                      placeholder="Paste document content here for analysis..."
                      value={documentContent}
                      onChange={(e) => setDocumentContent(e.target.value)}
                      className="min-h-[120px] bg-app border-slate-300"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      onClick={handleAnalyze}
                      disabled={isProcessing || (!documentContent.trim() && !uploadedFile)}
                      className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500"
                    >
                      {isProcessing ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                      ) : (
                        <><Search className="w-4 h-4 mr-2" /> Analyze</>
                      )}
                    </Button>
                    <Button variant="outline" onClick={clearAll} disabled={isProcessing}>
                      Clear
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
                <ShieldCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-600 mb-2">Select a Tool</h2>
                <p className="text-slate-400">Choose an AI-Authenticator tool above to get started</p>
              </div>
            )}
          </div>

          {/* Results Section */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-500" />
              Analysis Results
            </h2>

            {result ? (
              <ScrollArea className="h-[500px]">
                <div className="space-y-4">
                  {/* Confidence Score */}
                  <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                      <span className="font-medium text-slate-700">Analysis Complete</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Percent className="w-4 h-4 text-indigo-500" />
                      <span className="text-lg font-bold text-indigo-600">{result.confidence}%</span>
                      <span className="text-xs text-slate-500">confidence</span>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="p-4 bg-app rounded-lg border">
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Summary</h3>
                    <p className="text-sm text-slate-600">{result.summary}</p>
                  </div>

                  {/* Details */}
                  <div className="p-4 bg-app rounded-lg border">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Detailed Findings</h3>
                    <div className="space-y-2">
                      {result.details.map((detail, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 flex-shrink-0" />
                          <span className="text-sm text-slate-600">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tool-specific features */}
                  {activeTool?.id === "verify_documents" && (
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-sm text-emerald-700">
                      <Globe className="w-4 h-4" />
                      254+ countries &bull; 16,000+ document templates supported
                    </div>
                  )}

                  {activeTool?.id === "narrative_detector" && (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700">
                      <Newspaper className="w-4 h-4" />
                      Narrative purpose classification: agenda-driven, journalism, wiki, opinion, satire
                    </div>
                  )}

                  {/* Recommendations */}
                  {result.recommendations && result.recommendations.length > 0 && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <h3 className="text-sm font-semibold text-amber-800 mb-2">Recommendations</h3>
                      <ul className="space-y-1">
                        {result.recommendations.map((rec, idx) => (
                          <li key={idx} className="text-sm text-amber-700 flex items-start gap-2">
                            <span className="text-amber-500 mt-0.5">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <div className="h-[500px] flex items-center justify-center">
                <div className="text-center">
                  {isProcessing ? (
                    <Loader2 className="w-12 h-12 text-indigo-400 mx-auto mb-3 animate-spin" />
                  ) : (
                    <>
                      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">Results will appear here</p>
                      <p className="text-sm text-slate-400 mt-1">Select a tool and provide content to analyze</p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
